import {
  Button,
  Card,
  Descriptions,
  Dropdown,
  Empty,
  Image,
  Input,
  Modal,
  Spin,
  Tag,
  message,
} from 'antd';
import { MenuProps } from 'antd/es/menu';
import { DownOutlined, EditOutlined } from '@ant-design/icons';
import PageTitle from 'apps/home/components/PageTitle';
import apiCall, { apiUserCall } from 'apps/investment/apiCalls/user';
import apiUserIdentificationLogsCall from 'apps/investment/apiCalls/user/user-identification-logs';
import userEkycRecordsApiCall from 'apps/investment/apiCalls/user-ekyc-records';
import { columnsGenerator as ekycRecordColumns } from 'apps/investment/components/user-ekyc-records/config';
import { DataTable } from 'common/data-table';
import DataTablePagination from 'common/data-table-pagination';
import {
  cardTypeLabel,
  identificationStatusColors,
} from 'apps/investment/constants/user';
import ErrorMessage from 'common/ui/ErrorMessage';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import UIIconTooltip from 'common/ui/UIIconTooltip';
import { Label } from 'common/ui/label';
import MetaDecorator from 'apps/home/components/MetaDecorator';
import { useFetchApiGet, useFetchApiList, usePostApi } from 'hooks/api';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  investmentIdentificationImageStatusColors,
  investmentIdentificationImageStatusLabel,
  investmentIdentificationImageTypeLabels,
} from 'utils/constants';
import { formatDateTime } from 'utils/formatDate';
import FieldWarning from './field-warning';
import IdentificationPreviewModal from './identification-preview-modal';
import IndividualIdentificationDetail from './individual-identification-detail';
import { AutoKycResponse } from './types';

const { Item: DescItem } = Descriptions;

const AUTO_KYC_LOADING_MSG_KEY = 'autoKycLoading';

type KycDecision = 'approve' | 'reject';

const KYC_DECISION_TEXT: Record<
  KycDecision,
  { title: string; description: string; label: string; okText: string }
> = {
  approve: {
    title: 'Approve EKYC?',
    description:
      'The user will be marked as eKYC verified. This note is saved in the identification log.',
    label: 'Note (required)',
    okText: 'Approve EKYC',
  },
  reject: {
    title: 'Reject EKYC?',
    description:
      'The user will be marked as not verified and will have to submit eKYC again. This reason is saved in the identification log.',
    label: 'Reason (required)',
    okText: 'Reject EKYC',
  },
};

// Identity documents, ID images and bank details live on their own page so
// that opening a user's account page does not put them on screen.
export const InvestmentUserIdentificationDetailPage = () => {
  const { userId, id: identificationId } = useParams();
  const userUrl = `/investment/users/${userId}`;

  const {
    data: item,
    isLoading,
    error,
    load,
    refetch,
  } = useFetchApiGet(apiUserCall.detail, { resourceName: 'data' });

  useEffect(() => {
    if (userId) load({ id: userId });
  }, [userId]);

  if (isLoading || (!item && !error)) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage {...(error as any)} />;
  }

  const identification = (item as any)?.identification;
  if (identification?.id && identification.id !== identificationId) {
    return (
      <Navigate
        to={`${userUrl}/identifications/${identification.id}`}
        replace
      />
    );
  }

  return (
    <>
      <MetaDecorator title="User Identification" />
      <PageTitle
        title={`Xác thực danh tính - ${(item as any)?.name ||
          (item as any)?.email ||
          ''}`}
        backUrl={userUrl}
      />
      <div className="space-y-4">
        {identification?.id ? (
          <IdentificationDetail item={item} onRefresh={refetch} />
        ) : (
          <Empty description="Chưa có thông tin xác thực" />
        )}
        {/* Shown even without an identification: failed attempts still
            leave eKYC records. */}
        <UserEkycRecords userId={userId} />
      </div>
    </>
  );
};

// The user's eKYC provider checks, formerly their own page at
// /investment/users/:userId/user-ekyc-records.
const UserEkycRecords = ({ userId }: { userId?: string }) => {
  const [searchParams] = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const {
    data,
    paging,
    isLoading,
    error,
    load,
  } = useFetchApiList(userEkycRecordsApiCall.list, { resourceName: 'data' });

  useEffect(() => {
    if (userId) load({ ...query, userId });
  }, [userId, JSON.stringify(query)]);

  return (
    <Card size="small" title="Lịch sử xác thực eKYC">
      {error ? (
        <ErrorMessage errors={error} />
      ) : isLoading ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : (
        <>
          <DataTable
            noViewOptions
            columns={ekycRecordColumns({ userId })}
            data={data || []}
          />
          {paging && <DataTablePagination paging={paging} />}
        </>
      )}
    </Card>
  );
};

const IdentificationDetail = ({ item, onRefresh }) => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const { identification, id, ekyc_verified, re_kyc } = item || {};
  const { id: ID_id } = identification || {};

  // Approve/reject change the user record and the KYC sub-queries below.
  const refreshUserData = () => {
    onRefresh?.();
    queryClient.invalidateQueries({ queryKey: [apiCall.checkKyc.queryKey] });
    queryClient.invalidateQueries({ queryKey: [apiCall.IDImage.queryKey] });
  };

  const {
    post: onApprove,
    isLoading: isApproving,
  } = usePostApi(apiCall.approve, { onSuccess: refreshUserData });

  const { post: onReject, isLoading: isRejecting } = usePostApi(
    apiCall.reject,
    { onSuccess: refreshUserData }
  );

  // Approve and reject both open a confirmation dialog that asks for a
  // note; nothing is sent until the admin confirms there.
  const [decision, setDecision] = useState<KycDecision | null>(null);
  const [reason, setReason] = useState('');

  const openDecision = (next: KycDecision) => {
    setReason('');
    setDecision(next);
  };

  const confirmDecision = () => {
    const trimmed = reason.trim();
    if (!decision || !trimmed) return;
    const post = decision === 'approve' ? onApprove : onReject;
    post(
      { id, payload: { reason: trimmed } },
      { onSuccess: () => setDecision(null) }
    );
  };

  const isRejected = identification?.verification_status === 'rejected';

  const actionItems: MenuProps['items'] = [
    ...(!ekyc_verified
      ? [
          {
            key: 'approve',
            label: 'Approve EKYC',
            disabled: isApproving,
            onClick: () => openDecision('approve'),
          },
        ]
      : []),
    ...(re_kyc && ekyc_verified
      ? [
          {
            key: 're-kyc',
            label: 'Review Re-KYC',
            onClick: () =>
              navigate(
                `/investment/users/${item.id}/identifications/${item.account_type}/${ID_id}/re-kyc`
              ),
          },
        ]
      : []),
    {
      key: 'edit',
      label: 'Edit',
      onClick: () =>
        navigate(`/investment/users/${item.id}/identifications/${ID_id}/edit`),
    },
    { type: 'divider' },
    {
      key: 'reject',
      label: 'Reject EKYC',
      danger: true,
      disabled: isRejected || isRejecting,
      onClick: () => openDecision('reject'),
    },
  ];

  const {
    data: IDImage,
    load: loadIDImage,
    isLoading: isLoadingIDImage,
    error: loadIDImageError,
  } = useFetchApiList(apiCall.IDImage, { resourceName: 'data' });

  const {
    data: autoKyc,
    load: loadAutoKyc,
    isLoading: isLoadingAutoKyc,
    error: loadAutoKycError,
  } = useFetchApiGet<AutoKycResponse>(apiCall.checkKyc, {
    resourceName: 'data',
  });

  const {
    data: userIdentificationLogs,
    error: errorUserIdentificationLogs,
    load: loadUserIdentificationLogs,
  } = useFetchApiList(apiUserIdentificationLogsCall, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (id) {
      loadIDImage({ id });
      if (!ekyc_verified) {
        loadAutoKyc({ id });
      }
      loadUserIdentificationLogs({ user_id: id });
    }
  }, [id]);

  useEffect(() => {
    if (isLoadingAutoKyc) {
      messageApi.open({
        key: AUTO_KYC_LOADING_MSG_KEY,
        type: 'loading',
        content: 'Đang tải dữ liệu so sánh eKYC...',
        duration: 0,
      });
    } else {
      messageApi.destroy(AUTO_KYC_LOADING_MSG_KEY);
    }
  }, [isLoadingAutoKyc, messageApi]);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any | null>(null);

  return (
    <>
      {contextHolder}
      <Card
        size="small"
        title={
          <div className="flex flex-wrap items-center gap-1">
            <span>Xác thực danh tính</span>
            {isLoadingAutoKyc ? null : loadAutoKycError ? (
              <div>
                <Tag color="orange">
                  {'Lỗi Auto Approver: ' + (loadAutoKycError as any).error}
                </Tag>
              </div>
            ) : autoKyc?.total_message > 0 ? (
              <div className="flex flex-wrap gap-1 items-center">
                <Tag color="orange">
                  Phát hiện {autoKyc?.total_message} bất thường
                </Tag>
                {autoKyc?.field_warnings &&
                'face_matching' in autoKyc?.field_warnings ? (
                  <Tag color="orange">
                    {autoKyc?.field_warnings['face_matching']}
                  </Tag>
                ) : null}
              </div>
            ) : null}
          </div>
        }
        extra={
          <Dropdown menu={{ items: actionItems }} trigger={['click']}>
            <Button>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        }
      >
        <div className="relative space-y-4">
          {item.account_type == 'individual' ? (
            <div className="flex gap-4 lg:flex-row flex-col">
              <IndividualIdentificationDetail
                userIdentification={identification}
                autoKyc={autoKyc}
              />
            </div>
          ) : (
            <BusinessIdentificationDetail userIdentification={identification} />
          )}

          <Descriptions
            size="small"
            title={'Thông tin ngân hàng'}
            column={1}
            bordered
          >
            <DescItem label="Tên ngân hàng">
              {identification?.bank ? (
                <Link to={`/catalog/banks/${identification.bank?.id}`}>
                  {`${identification?.bank_name} (${identification.bank?.code})`}
                </Link>
              ) : identification?.bank_name ? (
                identification.bank_name
              ) : (
                '-'
              )}
            </DescItem>
            <DescItem label="Chủ tài khoản">
              {identification?.bank_account_name}
              {autoKyc?.field_warnings &&
              'bank_account_name' in autoKyc?.field_warnings ? (
                <FieldWarning
                  message={autoKyc?.field_warnings['bank_account_name']}
                />
              ) : null}
            </DescItem>
            <DescItem label="Số tài khoản">
              {identification?.bank_account_number}
              {autoKyc?.field_warnings &&
              'bank_account_number' in autoKyc?.field_warnings ? (
                <FieldWarning
                  message={autoKyc?.field_warnings['bank_account_number']}
                />
              ) : null}
            </DescItem>
            <DescItem label="Chi nhánh">
              {identification?.bank_branch_name}
            </DescItem>
          </Descriptions>

          <Card size="small" title="Ghi chú">
            <div className="max-h-[21vh] overflow-y-auto">
              {errorUserIdentificationLogs ? (
                <ErrorMessage errors={errorUserIdentificationLogs} />
              ) : userIdentificationLogs?.length > 0 ? (
                userIdentificationLogs.map((log, index) => (
                  <p key={index}>
                    <Tag color="green">{log.admin_email ?? 'Admin'}</Tag> chuyển
                    sang &nbsp;
                    <Tag
                      color={
                        identificationStatusColors[log.verification_status]
                      }
                    >
                      {log.verification_status_label || log.verification_status}
                    </Tag>
                    (Ghi chú: &nbsp;
                    <Tag color="grey">{log.note}</Tag>
                    {formatDateTime(log.created_at)})
                  </p>
                ))
              ) : (
                'Chưa có ghi chú'
              )}
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {isLoadingIDImage ? (
              [1, 2, 3].map(item => (
                <div
                  key={item}
                  className="w-full h-48 flex items-center justify-center bg-gray-100 overflow-hidden"
                >
                  <Spin size="large" />
                </div>
              ))
            ) : loadIDImageError ? (
              <div className="w-full h-48 flex items-center justify-center bg-gray-100 overflow-hidden">
                <ErrorMessage errors={loadIDImageError} />
              </div>
            ) : IDImage?.length > 0 ? (
              <>
                {[...IDImage].map(
                  ({
                    id,
                    data,
                    image_url,
                    image_type,
                    status,
                    error_message,
                  }) => {
                    const imageBase64 = `data:image/jpeg;base64,${data}`;
                    const previewSrc = image_url || imageBase64;
                    const isSelfie = image_type === 'selfie';
                    return (
                      <div
                        key={id}
                        className="w-full h-48 flex items-center justify-center bg-gray-100 overflow-hidden relative"
                      >
                        <Image
                          className={`w-full h-full object-contain cursor-pointer ${
                            isSelfie ? 'blur-xl scale-50' : 'scale-90'
                          }`}
                          src={previewSrc}
                          preview={false}
                          onClick={() => {
                            setSelectedImage({
                              id,
                              data,
                              image_url,
                              image_type,
                              status,
                              error_message,
                            });
                            setIsPreviewModalOpen(true);
                          }}
                        />

                        <div className="absolute top-0 left-0 right-0 px-3 py-2 bg-black/50 backdrop-blur-sm flex justify-between items-center">
                          <Label className="text-white m-0">
                            {
                              investmentIdentificationImageTypeLabels[
                                image_type
                              ]
                            }
                          </Label>
                          <Tag
                            color={
                              investmentIdentificationImageStatusColors[status]
                            }
                          >
                            {investmentIdentificationImageStatusLabel[status] ||
                              status}
                          </Tag>
                        </div>
                        {error_message !== '' && (
                          <div className="absolute bottom-0 right-0 px-3 py-2 bg-black/50 backdrop-blur-sm">
                            <Tag color="purple">{error_message}</Tag>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </>
            ) : null}
          </div>
        </div>
      </Card>

      <Modal
        title={decision ? KYC_DECISION_TEXT[decision].title : ''}
        open={!!decision}
        onCancel={() => setDecision(null)}
        onOk={confirmDecision}
        okText={decision ? KYC_DECISION_TEXT[decision].okText : 'OK'}
        okButtonProps={{
          danger: decision === 'reject',
          disabled: !reason.trim(),
          loading: decision === 'approve' ? isApproving : isRejecting,
        }}
        destroyOnClose
      >
        {decision ? (
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">
              {KYC_DECISION_TEXT[decision].description}
            </p>
            <label className="block text-sm font-medium">
              {KYC_DECISION_TEXT[decision].label}
            </label>
            <Input.TextArea
              autoFocus
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </div>
        ) : null}
      </Modal>

      <IdentificationPreviewModal
        open={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setSelectedImage(null);
        }}
        identification={identification}
        autoKyc={autoKyc}
        selectedImage={selectedImage}
      />
    </>
  );
};

const BusinessIdentificationDetail = ({ userIdentification }) => {
  if (!userIdentification)
    return <Empty description="Chưa có thông tin xác thực" />;

  return (
    <div className="flex flex-col gap-6 ">
      <Descriptions column={1} bordered>
        <DescItem label="Tên doanh nghiệp">
          {userIdentification.full_name}
        </DescItem>
        <DescItem label="Ngày đăng ký lần đầu">
          {userIdentification.date_of_birth}
        </DescItem>
        <DescItem label="Loại căn cước">
          {cardTypeLabel[userIdentification.card_type]}
        </DescItem>
        <DescItem label="Số chứng nhận đăng ký doanh nghiệp / Mã số thuế">
          {userIdentification.id_number}
        </DescItem>
        <DescItem label="Nơi cấp">{userIdentification.issue_place}</DescItem>
        <DescItem label="Trạng thái xác thực">
          <Tag
            color={
              identificationStatusColors[userIdentification.verification_status]
            }
          >
            {userIdentification.verification_status_label}
          </Tag>
        </DescItem>
        <DescItem label="Ghi chú">
          {userIdentification?.identification_logs?.length > 0
            ? userIdentification.identification_logs.map((log, index) => (
                <p key={index}>
                  <Tag color="green">{log.admin_email ?? 'Admin'}</Tag> chuyển
                  sang &nbsp;
                  <Tag
                    color={identificationStatusColors[log.verification_status]}
                  >
                    {log.verification_status_label}
                  </Tag>
                  (Ghi chú:{' '}
                  <UIIconTooltip
                    size="small"
                    title="Edit"
                    Icon={EditOutlined}
                  />{' '}
                  &nbsp;
                  <Tag color="grey">{log.note}</Tag>
                  {formatDateTime(log.created_at)})
                </p>
              ))
            : '-'}
        </DescItem>
      </Descriptions>
      <Descriptions title="Địa chỉ đăng ký kinh doanh" column={1} bordered>
        <DescItem label="Thành phố/Tỉnh">
          {userIdentification.residential_address_city ? (
            <Link
              to={`/investment/provinces/${userIdentification.residential_address_city.id}`}
            >
              {userIdentification.residential_address_city
                ? userIdentification.residential_address_city.name
                : userIdentification.residential_address_city.id}
            </Link>
          ) : userIdentification.residential_address_city_string ? (
            userIdentification.residential_address_city_string
          ) : (
            '-'
          )}
        </DescItem>
        <DescItem label="Quận/Huyện">
          {userIdentification.residential_address_district ? (
            <Link
              to={`/investment/districts/${userIdentification.residential_address_district.id}`}
            >
              {userIdentification.residential_address_district
                ? userIdentification.residential_address_district.name
                : '-'}
            </Link>
          ) : userIdentification.residential_address_district_string ? (
            userIdentification.residential_address_district_string
          ) : (
            '-'
          )}
        </DescItem>
        <DescItem label="Địa chỉ">
          {userIdentification.residential_address}
        </DescItem>
      </Descriptions>

      <Descriptions title={'Địa chỉ liên hệ'} column={1} bordered>
        <DescItem label="Thành phố/Tỉnh">
          {userIdentification.address_city ? (
            <Link
              to={`/investment/provinces/${userIdentification.address_city.id}`}
            >
              {userIdentification.address_city.name
                ? userIdentification.address_city.name
                : userIdentification.address_city.id}
            </Link>
          ) : userIdentification.address_city_string ? (
            userIdentification.address_city_string
          ) : (
            '-'
          )}
        </DescItem>
        <DescItem label="Quận/Huyện">
          {userIdentification.address_district ? (
            <Link
              to={`/investment/districts/${userIdentification.address_district.id}`}
            >
              {userIdentification.address_district_string
                ? userIdentification.address_district_string
                : userIdentification.address_district.id}
            </Link>
          ) : userIdentification.address_district_string ? (
            userIdentification.address_district_string
          ) : (
            '-'
          )}
        </DescItem>
        <DescItem label="Địa chỉ">{userIdentification.address}</DescItem>
      </Descriptions>
    </div>
  );
};
